import { RefObject, useCallback, useEffect, useRef } from 'react';
import { isTurnstileEnabled, mountTurnstile, TurnstileError } from 'pages/Auth/utils/turnstile/turnstile';
import type { MountedTurnstile } from 'pages/Auth/utils/turnstile/turnstile.types';

const useTurnstileToken = (action: string, hostRef: RefObject<HTMLElement | null>) => {
  const mountedRef = useRef<MountedTurnstile | null>(null);
  const mountPromiseRef = useRef<Promise<MountedTurnstile | null> | null>(null);
  const enabled = isTurnstileEnabled();

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let cancelled = false;

    mountPromiseRef.current = (async () => {
      const host = hostRef.current;
      if (!host || cancelled) {
        return null;
      }
      try {
        const mounted = await mountTurnstile(action, host);
        if (cancelled) {
          mounted.remove();
          return null;
        }
        mountedRef.current = mounted;
        return mounted;
      } catch {
        mountedRef.current = null;
        return null;
      }
    })();

    return () => {
      cancelled = true;
      mountedRef.current?.remove();
      mountedRef.current = null;
      mountPromiseRef.current = null;
    };
  }, [action, enabled, hostRef]);

  const getToken = useCallback(async () => {
    if (!enabled) {
      return undefined;
    }

    let mounted = mountedRef.current ?? (await mountPromiseRef.current);
    if (!mounted && hostRef.current) {
      mounted = await mountTurnstile(action, hostRef.current);
      mountedRef.current = mounted;
    }
    if (!mounted) {
      throw new TurnstileError();
    }
    return mounted.getToken();
  }, [action, enabled, hostRef]);

  const reset = useCallback(() => {
    mountedRef.current?.reset();
  }, []);

  /** Get a token, run `fn`, then reset the widget (tokens are single-use). */
  const withToken = useCallback(
    async <T,>(fn: (token?: string) => Promise<T>): Promise<T> => {
      try {
        return await fn(await getToken());
      } finally {
        reset();
      }
    },
    [getToken, reset]
  );

  return { getToken, reset, withToken, isEnabled: enabled };
};

export default useTurnstileToken;
